import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Printer, Settings, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { QuickSettings } from '@/components/quick-settings'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { WasteDialog } from '@/components/waste-dialog'
import { gqlRequest, graphqlErrorMessage, type RequestSession } from '@/lib/api/client'
import {
  AcceptOrderMutation,
  ClearPreparingLineItemMutation,
  DeclineOrderMutation,
  FinishOrdersMutation,
  FinishPreparingLineItemMutation,
  FinishPreparingSlipMutation,
  KitchenDisplayDataQuery,
  KitchenMenuQuery,
  MarkOrderAsPickedUpMutation,
  PrintDigitalSlipMutation,
  StartPreparingLineItemMutation,
} from '@/lib/api/operations'
import { type ScreenSettings, screenSettings } from '@/lib/configuration'
import { countOutstanding } from '@/lib/item-counter'
import { completeSlipForDisplay } from '@/lib/kds-actions'
import { COLOR_SCHEME_STYLE, columnCount, FONT_STACKS, LAYOUT_SCALE, showsSlipCompleteButton } from '@/lib/kds-settings'
import { kitchenFlowIndex } from '@/lib/kitchen-components'
import { printLabels } from '@/lib/labels'
import { type ItemAction, itemSlipState } from '@/lib/preparation'
import { createRecord, loadRecords, readTicketTimes, recordDate, ticketSeconds } from '@/lib/records'
import { dueForAutoPickup, type Ghosts, ghostSlips, hideLongFinished, type ReadySince, trackReady, updateGhosts } from '@/lib/retention'
import { filterChannels, isPreorder, lateColor, resolvedDisplayMode, type Slip, slipsForDisplay, sortSlips } from '@/lib/slips'
import { prepareSnapshot, recallSnapshot, slipSnapshotSchema } from '@/lib/snapshots'
import { newSlipIds, playNewOrderChime, unlockSound } from '@/lib/sound'
import { useConfiguration } from '@/lib/useConfiguration'
import { ItemCounterPanel } from './item-counter-panel'
import { SlipCard } from './slip-card'

type Tab = 'now' | 'waiting' | 'preparing' | 'done' | 'ready' | 'preorders' | 'history'
export function KitchenDisplay({ session, restaurantId, kitchenDisplayId }: { session: RequestSession; restaurantId: string; kitchenDisplayId: string }) {
  const queryClient = useQueryClient()
  const configuration = useConfiguration(session, restaurantId)
  const config = configuration.config
  const [overrides, setOverrides] = useState<Partial<ScreenSettings>>({})
  const settings = useMemo(() => ({ ...screenSettings(config, kitchenDisplayId), ...overrides }), [config, kitchenDisplayId, overrides])
  const [now, setNow] = useState(Date.now())
  const [width, setWidth] = useState(window.innerWidth)
  const [historyDate, setHistoryDate] = useState(recordDate(Date.now()))
  const [tab, setTab] = useState<Tab>('now')
  const [quickSettings, setQuickSettings] = useState(false)
  const [waste, setWaste] = useState(false)
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())
  const running = useRef(new Set<string>())
  const [actionError, setActionError] = useState<string | null>(null)
  const [printSlip, setPrintSlip] = useState<Slip | null>(null)
  const [updateAvailable, setUpdateAvailable] = useState(false)
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    const resize = () => setWidth(window.innerWidth)
    const update = () => setUpdateAvailable(true)
    window.addEventListener('resize', resize)
    window.addEventListener('galley:update-available', update)
    return () => {
      clearInterval(timer)
      window.removeEventListener('resize', resize)
      window.removeEventListener('galley:update-available', update)
    }
  }, [])
  const dataQuery = useQuery({
    queryKey: ['kdsData', session.mode, restaurantId, kitchenDisplayId],
    refetchInterval: settings.pollIntervalSeconds * 1000,
    queryFn: () => gqlRequest(session, KitchenDisplayDataQuery, { restaurantId, kitchenDisplayId }),
  })
  const menuQuery = useQuery({ queryKey: ['kitchenMenu', session.mode, restaurantId], refetchInterval: 60_000, queryFn: () => gqlRequest(session, KitchenMenuQuery, { restaurantId }) })
  const historyQuery = useQuery({
    queryKey: ['history', session.mode, restaurantId, historyDate],
    enabled: config.ticketTimeEnabled,
    refetchInterval: 30_000,
    queryFn: () => loadRecords(session, restaurantId, `galley.ticketTime.${historyDate}`),
  })
  const history = useMemo(() => readTicketTimes(historyQuery.data ?? []), [historyQuery.data])
  const flowIndex = useMemo(() => kitchenFlowIndex(menuQuery.data?.restaurant?.menu?.pages?.flatMap((p) => p.categories?.flatMap((c) => c.products ?? []) ?? []) ?? []), [menuQuery.data])
  const display = dataQuery.data?.restaurant?.kitchenDisplay
  const stations = dataQuery.data?.restaurant?.kitchenDisplays ?? []
  const mode = resolvedDisplayMode(settings.displayMode, kitchenDisplayId)
  const visible = useMemo(
    () => sortSlips(filterChannels(slipsForDisplay(display?.slips ?? [], mode), settings.showOnlyChannels), mode, display?.slipSortOrder ?? undefined),
    [display, mode, settings.showOnlyChannels],
  )
  const readySince = useRef<ReadySince>(new Map())
  const ghosts = useRef<Ghosts>(new Map())
  const recallKey = `galley.recalled/${session.mode}/${restaurantId}/${kitchenDisplayId}`
  const [recalled, setRecalled] = useState<Map<string, Slip>>(() => {
    try {
      const stored: unknown = JSON.parse(sessionStorage.getItem(recallKey) ?? '[]')
      if (!Array.isArray(stored)) return new Map()
      const parsed = stored.flatMap((value) => {
        const parsed = slipSnapshotSchema.safeParse(value)
        return parsed.success ? [parsed.data] : []
      })
      return new Map(parsed.map((slip) => [slip.id, slip]))
    } catch {
      return new Map()
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem(recallKey, JSON.stringify([...recalled.values()]))
    } catch {
      /* A full/blocked store must not interrupt kitchen actions. */
    }
  }, [recallKey, recalled])
  const [localRevision, setLocalRevision] = useState(0)
  const [clearedGhosts, setClearedGhosts] = useState<ReadonlySet<string>>(new Set())
  readySince.current = trackReady(readySince.current, visible, now)
  ghosts.current = updateGhosts(ghosts.current, visible, now, settings.keepPaidSlips, settings.keepPaidSlipsAutoExpireMin, clearedGhosts)
  const kept = [...ghostSlips(ghosts.current, visible), ...[...recalled.values()].filter((slip) => !visible.some((live) => live.id === slip.id))]
  const keptIds = new Set(kept.map((slip) => slip.id))
  const retained = hideLongFinished([...visible, ...kept], readySince.current, now, settings.hideBumpedAfterSeconds)
  const preorders = retained.filter((slip) => isPreorder(slip, now, settings.preorderThresholdMinutes))
  const queued = retained.filter((slip) => !isPreorder(slip, now, settings.preorderThresholdMinutes))
  const shown = tab === 'preorders' ? preorders : tab === 'now' || tab === 'history' ? queued : queued.filter((slip) => itemSlipState(slip, mode) === tab)
  const counted = countOutstanding(queued, mode, config.counter, settings.itemCounterIncludeAlternatives, kitchenDisplayId)
  const announced = useRef<Set<string> | null>(null)
  useEffect(() => {
    const ids = queued.map((slip) => slip.id)
    const fresh = newSlipIds(announced.current, ids)
    announced.current = new Set(ids)
    if (fresh.length && settings.soundOnNewOrder) playNewOrderChime(settings.soundVolume)
  }, [queued, settings.soundOnNewOrder, settings.soundVolume])
  useEffect(() => {
    const wake = () => unlockSound()
    window.addEventListener('pointerdown', wake)
    return () => window.removeEventListener('pointerdown', wake)
  }, [])
  const run = useCallback(
    async (id: string, work: () => Promise<unknown>) => {
      if (running.current.has(id)) return
      running.current.add(id)
      setBusy(new Set(running.current))
      setActionError(null)
      try {
        await work()
        await queryClient.invalidateQueries({ queryKey: ['kdsData'] })
      } catch (cause) {
        setActionError(graphqlErrorMessage(cause))
      } finally {
        running.current.delete(id)
        setBusy(new Set(running.current))
      }
    },
    [queryClient],
  )
  const recordCompletion = async (slip: Slip) => {
    if (!config.ticketTimeEnabled) return
    try {
      await createRecord(session, restaurantId, `galley.ticketTime.${recordDate(Date.now())}`, {
        displayId: kitchenDisplayId,
        slipId: slip.id,
        completedAt: new Date().toISOString(),
        createdAt: slip.createdAt ?? new Date().toISOString(),
        orderCode: slip.humanOrderCode ?? null,
        transactionChannel: slip.transactionChannel ?? null,
        orderType: slip.orderType ?? null,
        seconds: ticketSeconds(slip.createdAt, slip.deliveryTime, Date.now()),
        slip,
      })
      await queryClient.invalidateQueries({ queryKey: ['history'] })
    } catch (error) {
      setActionError(`Beställningen är klar, men historiken kunde inte sparas: ${graphqlErrorMessage(error)}`)
    }
  }
  const finish = (slip: Slip) =>
    run(slip.id, async () => {
      const needsPrint = slip.lineItemSections?.some((section) => section?.items?.some((item) => item?.needsKitchenPrint))
      let cancelKitchenPrint = false
      if (kitchenDisplayId === 'main' && needsPrint) {
        if (!window.confirm('Beställningen har inte skrivits ut i köket. Markera klar utan utskrift?')) return
        cancelKitchenPrint = true
      }
      await completeSlipForDisplay(
        kitchenDisplayId,
        () => gqlRequest(session, FinishOrdersMutation, { restaurantId, digitalSlipId: slip.id, input: { cancelKitchenPrint } }),
        () => gqlRequest(session, FinishPreparingSlipMutation, { restaurantId, digitalSlipId: slip.id }),
      )
      await recordCompletion(slip)
    })
  const itemAction = (slip: Slip, itemId: string, action: ItemAction) =>
    run(slip.id, async () => {
      if (keptIds.has(slip.id)) {
        const next = prepareSnapshot(slip, itemId, action, new Date().toISOString())
        if (recalled.has(slip.id)) setRecalled((previous) => new Map(previous).set(slip.id, next))
        else {
          const tracked = ghosts.current.get(slip.id)
          if (tracked) ghosts.current.set(slip.id, { ...tracked, slip: next })
          setLocalRevision(localRevision + 1)
        }
        return
      }
      const variables = { restaurantId, digitalSlipId: slip.id, lineItemId: itemId }
      if (action === 'start') return gqlRequest(session, StartPreparingLineItemMutation, variables)
      if (action === 'finish') return gqlRequest(session, FinishPreparingLineItemMutation, variables)
      return gqlRequest(session, ClearPreparingLineItemMutation, variables)
    })
  const print = useCallback(
    (slip: Slip, printerQueueId: string) =>
      run(slip.id, async () => {
        await gqlRequest(session, PrintDigitalSlipMutation, { restaurantId, digitalSlipId: slip.id, printerQueueId })
        await printLabels(session, restaurantId, kitchenDisplayId, slip, config.labels)
        setPrintSlip(null)
      }),
    [session, restaurantId, kitchenDisplayId, config.labels, run],
  )
  const autoPicked = useRef(new Set<string>())
  useEffect(() => {
    if (kitchenDisplayId !== 'main') return
    const present = new Set(visible.map((slip) => slip.id))
    for (const id of autoPicked.current) if (!present.has(id)) autoPicked.current.delete(id)
    for (const id of dueForAutoPickup(visible, readySince.current, now, settings.autoMarkPickedUpAfterSec)) {
      if (autoPicked.current.has(id) || running.current.has(id)) continue
      autoPicked.current.add(id)
      run(id, () =>
        gqlRequest(session, MarkOrderAsPickedUpMutation, { restaurantId, digitalSlipId: id }).catch((error) => {
          throw error
        }),
      )
    }
  }, [visible, now, settings.autoMarkPickedUpAfterSec, kitchenDisplayId, restaurantId, session, run])
  const automaticallyPrinted = useRef<Set<string> | null>(null)
  useEffect(() => {
    const ids = new Set(queued.map((slip) => slip.id))
    const fresh = newSlipIds(automaticallyPrinted.current, [...ids])
    automaticallyPrinted.current = ids
    if (!settings.automaticallyPrintNewSlips || !display?.printerQueue) return
    for (const id of fresh) {
      const slip = queued.find((s) => s.id === id)
      if (slip) print(slip, display.printerQueue.id)
    }
  }, [queued, settings.automaticallyPrintNewSlips, display?.printerQueue, print])
  if (configuration.isPending || dataQuery.isPending)
    return (
      <p className="p-6" role="status">
        Laddar beställningar…
      </p>
    )
  if (configuration.error)
    return (
      <p className="p-6 text-destructive" role="alert">
        {graphqlErrorMessage(configuration.error)}
      </p>
    )
  const scheme = COLOR_SCHEME_STYLE[settings.colorScheme]
  const scale = (settings.fontSizeMultiplier / 100) * LAYOUT_SCALE[settings.layout]
  const widthAvailable = width - (settings.showItemCounter && !settings.articleCounterOnly ? settings.itemCounterWidth : 0) - 24
  const today = recordDate(now)
  const stationHistory = history.filter((row) => row.displayId === kitchenDisplayId && recordDate(Date.parse(row.completedAt)) === historyDate)
  const average = stationHistory.length ? Math.round(stationHistory.reduce((sum, row) => sum + row.seconds, 0) / stationHistory.length) : 0
  return (
    <div className={`flex min-h-0 flex-1 flex-col bg-background text-foreground ${scheme.root}`} style={{ fontFamily: FONT_STACKS[settings.fontFamily], ...scheme.vars }}>
      <div className="flex flex-wrap items-center gap-2 border-b p-2">
        {(
          [
            ['now', 'Aktiva'],
            ['waiting', 'Ej påbörjade'],
            ['preparing', 'Påbörjade'],
            ['done', 'Tillagade'],
            ['ready', 'Hämtklara'],
            ['preorders', `Förbokade (${preorders.length})`],
            ['history', 'Historik'],
          ] as const
        ).map(([value, label]) => (
          <Button key={value} variant={tab === value ? 'default' : 'ghost'} onClick={() => setTab(value)}>
            {label}
          </Button>
        ))}
        <span className="ml-auto text-sm tabular-nums" title={`Genomsnitt från beställning till klar · ${historyDate}`}>
          TT {Math.floor(average / 60)}:{String(average % 60).padStart(2, '0')} · {stationHistory.length} bongar
        </span>
        {config.wasteEnabled && (
          <Button variant="ghost" onClick={() => setWaste(true)}>
            <Trash2 />
            Svinn
          </Button>
        )}
        <Button variant="ghost" onClick={() => setQuickSettings(true)}>
          <Settings />
          Inställningar
        </Button>
      </div>
      {updateAvailable && <p className="border-b bg-primary/10 px-4 py-2">En ny version finns. Tryck på uppdateringsknappen när köket kan avbrytas.</p>}
      {dataQuery.error && (
        <p role="alert" className="bg-destructive/10 px-4 py-2 text-destructive">
          Anslutningen bröts. Visar senast hämtade beställningar · {graphqlErrorMessage(dataQuery.error)}
        </p>
      )}
      {actionError && (
        <div role="alert" className="flex items-center justify-between gap-3 bg-destructive/10 px-4 py-2 text-destructive">
          <span>{actionError}</span>
          <Button variant="ghost" onClick={() => setActionError(null)}>
            Stäng
          </Button>
        </div>
      )}
      {tab === 'history' ? (
        <div className="flex-1 overflow-auto p-4">
          <h2 className="mb-3 font-bold">Klara beställningar</h2>
          <label>
            Datum{' '}
            <input
              className="mb-3 rounded border bg-card p-2"
              type="date"
              value={historyDate}
              max={today}
              onChange={(event) => {
                if (event.target.value) setHistoryDate(event.target.value)
              }}
            />
          </label>
          {historyQuery.error && <p role="alert">{graphqlErrorMessage(historyQuery.error)}</p>}
          {stationHistory
            .slice()
            .sort((a, b) => b.completedAt.localeCompare(a.completedAt))
            .map((row) => (
              <div key={`${row.displayId}/${row.slipId}`} className="flex gap-4 border-b py-3">
                <strong>{row.orderCode}</strong>
                <span>{new Date(row.completedAt).toLocaleTimeString('sv-SE')}</span>
                <span>
                  {Math.floor(row.seconds / 60)} min {row.seconds % 60} sek
                </span>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (visible.some((slip) => slip.id === row.slipId)) {
                      setActionError('Bongen finns redan på skärmen.')
                      return
                    }
                    const snapshot = recallSnapshot(row.slip)
                    if (!snapshot) {
                      setActionError('Bongens sparade innehåll kunde inte läsas.')
                      return
                    }
                    setClearedGhosts((previous) => new Set([...previous].filter((id) => id !== snapshot.id)))
                    setRecalled((previous) => new Map(previous).set(snapshot.id, snapshot))
                    setTab('now')
                  }}
                >
                  Återställ kopia
                </Button>
              </div>
            ))}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {settings.showItemCounter && settings.articleCounterOnly ? (
            <ItemCounterPanel lines={counted} width={settings.itemCounterWidth} rowSize={settings.itemCounterRowSize} fullScreen />
          ) : (
            <>
              {!shown.length ? (
                <p className="flex flex-1 items-center justify-center p-10 text-lg text-muted-foreground">Inga beställningar i denna vy.</p>
              ) : (
                <div className="grid min-h-0 flex-1 auto-rows-min gap-3 overflow-y-auto p-3" style={{ gridTemplateColumns: `repeat(${columnCount(settings, widthAvailable)}, minmax(0, 1fr))` }}>
                  {shown.map((slip) => (
                    <SlipCard
                      key={slip.id}
                      slip={slip}
                      flowIndex={flowIndex}
                      stationNames={new Map(stations.map((s) => [s.id, s.name ?? 'Station']))}
                      mode={mode}
                      settings={settings}
                      scale={scale}
                      now={now}
                      lateColor={display ? lateColor(display, Math.floor((now - Date.parse(slip.createdAt ?? '')) / 1000)) : null}
                      tillColor={config.tillColors.find((color) => color.tillId === slip.till?.id) ?? null}
                      showsPrint={!keptIds.has(slip.id) && (display?.showsPrintButton === true || config.counter.printWorkflowEnabled)}
                      showsComplete={showsSlipCompleteButton(settings, mode)}
                      finishLabel={kitchenDisplayId === 'main' ? 'Klar för hämtning' : 'Station klar'}
                      kept={keptIds.has(slip.id)}
                      recalled={recalled.has(slip.id)}
                      onClearKept={() => {
                        if (!recalled.has(slip.id)) recordCompletion(slip)
                        setRecalled((previous) => new Map([...previous].filter(([id]) => id !== slip.id)))
                        setClearedGhosts((previous) => new Set(previous).add(slip.id))
                      }}
                      busy={busy.has(slip.id)}
                      onFinishSlip={() => finish(slip)}
                      onPickedUp={() => run(slip.id, () => gqlRequest(session, MarkOrderAsPickedUpMutation, { restaurantId, digitalSlipId: slip.id }))}
                      onPrint={() => (display?.printerQueue ? print(slip, display.printerQueue.id) : setPrintSlip(slip))}
                      displayId={kitchenDisplayId}
                      skipStart={display?.skipExplicitSlipPreparationStart === true}
                      counterConfig={config.counter}
                      onItemAction={(itemId, action) => itemAction(slip, itemId, action)}
                      onAccept={() => run(slip.id, () => gqlRequest(session, AcceptOrderMutation, { restaurantId, digitalSlipId: slip.id }))}
                      onDecline={() => {
                        if (window.confirm('Avvisa beställningen?')) run(slip.id, () => gqlRequest(session, DeclineOrderMutation, { restaurantId, digitalSlipId: slip.id }))
                      }}
                    />
                  ))}
                </div>
              )}
              {settings.showItemCounter && <ItemCounterPanel lines={counted} width={settings.itemCounterWidth} rowSize={settings.itemCounterRowSize} fullScreen={false} />}
            </>
          )}
        </div>
      )}
      <QuickSettings
        open={quickSettings}
        onClose={() => setQuickSettings(false)}
        settings={settings}
        update={(next) => setOverrides((previous) => ({ ...previous, ...next }))}
        reset={() => setOverrides({})}
        restaurantId={restaurantId}
        displays={stations}
      />
      <WasteDialog open={waste} onClose={() => setWaste(false)} session={session} restaurantId={restaurantId} />
      <Dialog
        open={Boolean(printSlip)}
        onOpenChange={(open) => {
          if (!open) setPrintSlip(null)
        }}
      >
        <DialogContent>
          <DialogTitle>Välj skrivare</DialogTitle>
          <DialogDescription>Bongen skickas till vald skrivarkö.</DialogDescription>
          <Printer />
          {(dataQuery.data?.restaurant?.printerQueues ?? []).map((queue) => (
            <Button
              key={queue.id}
              disabled={printSlip ? busy.has(printSlip.id) : false}
              onClick={() => {
                if (printSlip) print(printSlip, queue.id)
              }}
            >
              {queue.name}
            </Button>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  )
}
